import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, Loader2, Sparkles, ShieldCheck, ArrowRight, Crown, ExternalLink, Rocket } from "lucide-react";

const PRICE = { basic: "$9.99", pro: "$99.99", org: "$999" };
const FEATURES = {
  basic: ["Up to 3 agents", "Wallet-backed hard caps", "Branded subdomain deploys", "Community support"],
  pro: ["Up to 15 agents", "Autopilot + child spawning", "Team seats (RBAC)", "Weekly revenue digest", "Priority email support"],
  org: ["Up to 100 agents", "Everything in Pro", "Advanced audit & controls", "Dedicated onboarding", "SLAs"],
};

export default function Billing() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [goLive, setGoLive] = useState(null);
  const [leadCfg, setLeadCfg] = useState(null);
  const [busy, setBusy] = useState("");
  const [compEmail, setCompEmail] = useState("");

  const load = () => {
    api.get("/billing").then((r) => setData(r.data)).catch(() => {});
    api.get("/go-live").then((r) => setGoLive(r.data)).catch(() => {});
    api.get("/public-lead-config").then((r) => setLeadCfg(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Handle return from Stripe checkout
  useEffect(() => {
    const sid = params.get("session_id");
    const plan = params.get("plan");
    if (params.get("canceled")) { toast("Checkout canceled"); setParams({}); return; }
    if (sid) {
      (async () => {
        setBusy("verify");
        try {
          const { data: st } = await api.get(`/billing/status/${sid}`);
          if (st.payment_status === "paid") { toast.success(`You're on the ${st.plan?.toUpperCase()} plan 🎉`); }
          else { toast("Payment still processing — refresh in a moment"); }
        } catch { toast.error("Could not verify payment"); }
        setParams({}); setBusy(""); load();
      })();
    } else if (plan) {
      startCheckout(plan);
      setParams({});
    }
    // eslint-disable-next-line
  }, []);

  const startCheckout = async (plan) => {
    setBusy(plan);
    try {
      const { data: r } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy("");
    }
  };

  const comp = async () => {
    if (!compEmail) return;
    try {
      const { data: r } = await api.post("/admin/comp", { email: compEmail });
      toast.success(`Comped ${compEmail} → ${r.granted.toUpperCase()} (unlimited)`);
      setCompEmail("");
    } catch (e) { toast.error(e.response?.data?.detail || "Comp failed"); }
  };

  if (!data) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;

  const current = data.comped ? "org" : (data.plan === "free" ? null : data.plan);

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <h1 className="font-display text-2xl font-black tracking-tight">Billing & Plan</h1>
        <p className="text-sm text-slate-400 mt-1">
          Current plan: <span className="text-cyan-400 font-semibold uppercase" data-testid="current-plan">{data.comped ? "ORG · COMPED" : (data.plan || "free").toUpperCase()}</span>
          {" · "}<span data-testid="agent-usage">{data.agents_used}/{data.agent_cap >= 1e8 ? "∞" : data.agent_cap} agents used</span>
        </p>
      </div>

      {goLive && (
        <div className="hud-card accent-top rounded-xl p-6" data-testid="go-live-checklist" style={{ "--accent-line": goLive.ready ? "#10B981" : "#06B6D4" }}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Rocket className={`w-5 h-5 ${goLive.ready ? "text-emerald-400" : "text-cyan-400"}`} />
              <h2 className="font-display font-bold">Go-Live Checklist</h2>
            </div>
            <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400" data-testid="go-live-progress">{goLive.done}/{goLive.total} ready{goLive.ready ? " · 🚀 production-ready" : ""}</span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${(goLive.done / goLive.total) * 100}%`, backgroundColor: goLive.ready ? "#10B981" : "#06B6D4" }} />
          </div>
          <div className="mt-4 space-y-1.5">
            {goLive.steps.map((s) => (
              <button key={s.key} data-testid={`go-live-step-${s.key}`} onClick={() => navigate(`/${s.action}`)}
                className="w-full flex items-start gap-3 rounded-md px-3 py-2.5 text-left hover:bg-slate-800/40 transition-colors">
                {s.done ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> : <Circle className="w-4 h-4 text-slate-600 shrink-0 mt-0.5" />}
                <span className="min-w-0">
                  <span className={`block text-sm font-medium ${s.done ? "text-slate-400 line-through" : "text-slate-100"}`}>{s.label}</span>
                  <span className="block text-[11px] text-slate-500 mt-0.5">{s.hint}</span>
                </span>
                {!s.done && <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0 mt-1" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {leadCfg && <LeadCaptureCard cfg={leadCfg} />}

      <div className="grid md:grid-cols-3 gap-4">
        {["basic", "pro", "org"].map((k) => {
          const isCurrent = current === k;
          const hi = k === "pro";
          return (
            <div key={k} data-testid={`plan-card-${k}`} className={`hud-card accent-top rounded-xl p-6 flex flex-col ${hi ? "ring-1 ring-cyan-500/40" : ""}`} style={{ "--accent-line": hi ? "#06B6D4" : "#334155" }}>
              <div className="flex items-center justify-between">
                <span className="font-display font-black text-lg capitalize">{k}</span>
                {hi && <Sparkles className="w-4 h-4 text-cyan-400" />}
              </div>
              <div className="mt-2 font-display text-3xl font-black">{PRICE[k]}<span className="text-xs text-slate-500 font-mono">/mo</span></div>
              <ul className="mt-4 space-y-2 flex-1">
                {FEATURES[k].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[13px] text-slate-300"><CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              <button
                data-testid={`subscribe-${k}`}
                disabled={isCurrent || busy === k}
                onClick={() => startCheckout(k)}
                className={`mt-5 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-bold transition-colors disabled:opacity-50 ${isCurrent ? "border border-emerald-500/40 text-emerald-400" : hi ? "bg-cyan-500 text-[#080B11] hover:bg-cyan-400" : "border border-slate-700 text-slate-100 hover:border-cyan-500/50"}`}
              >
                {busy === k ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrent ? <>Current plan</> : <>Subscribe <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          );
        })}
      </div>

      {data.portal_url && (
        <div className="hud-card rounded-xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid="manage-subscription">
          <div>
            <div className="font-display font-bold text-sm">Manage your subscription</div>
            <p className="text-xs text-slate-500 mt-0.5">Update your card, view invoices, or cancel anytime in the secure Stripe portal.</p>
          </div>
          <a href={data.portal_url} target="_blank" rel="noopener noreferrer" data-testid="portal-link"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-700 text-slate-100 font-semibold text-sm hover:border-cyan-500/50 hover:text-cyan-300 transition-colors">
            <ExternalLink className="w-4 h-4" /> Open billing portal
          </a>
        </div>
      )}

      {data.is_super_admin && (
        <div className="hud-card rounded-xl p-6" data-testid="comp-panel">
          <div className="flex items-center gap-2 mb-1"><Crown className="w-5 h-5 text-amber-400" /><h2 className="font-display font-bold">Super-admin · comp a user</h2></div>
          <p className="text-xs text-slate-500 mb-4">Grant any user's workspace free unlimited access (Org, bypasses the paywall).</p>
          <div className="flex gap-2 max-w-md">
            <input data-testid="comp-email-input" value={compEmail} onChange={(e) => setCompEmail(e.target.value)} placeholder="user@email.com" className="flex-1 px-3 py-2.5 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60" />
            <button data-testid="comp-submit" onClick={comp} className="px-4 py-2.5 rounded-md bg-amber-500 text-[#080B11] font-bold text-sm hover:bg-amber-400 transition-colors flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Comp → Org</button>
          </div>
        </div>
      )}
    </div>
  );
}

function LeadCaptureCard({ cfg }) {
  const snippet = `<!-- Kleen-G lead capture — paste on your ad landing page -->
<form action="${cfg.endpoint}" method="POST" onsubmit="event.preventDefault();
  fetch('${cfg.endpoint}',{method:'POST',headers:{'Content-Type':'application/json'},
  body:JSON.stringify({key:'${cfg.key}',name:this.name.value,email:this.email.value,
  interest:this.interest.value,website:this.website.value,
  utm_source:new URLSearchParams(location.search).get('utm_source')||'',
  utm_campaign:new URLSearchParams(location.search).get('utm_campaign')||''})})
  .then(()=>this.reset()||alert('Thanks — we will be in touch!'));">
  <input name="name" placeholder="Your name" required />
  <input name="email" type="email" placeholder="Email" required />
  <input name="interest" placeholder="What do you need?" />
  <input name="website" style="display:none" tabindex="-1" autocomplete="off" />
  <button type="submit">Get my site</button>
</form>`;
  const copy = (t, label) => { navigator.clipboard?.writeText(t).then(() => toast.success(`${label} copied`)).catch(() => {}); };
  return (
    <div className="hud-card rounded-xl p-6" data-testid="lead-capture-card">
      <div className="flex items-center gap-2 mb-1">
        <ArrowRight className="w-5 h-5 text-cyan-400" />
        <h2 className="font-display font-bold">Lead Capture — wire your ad funnel</h2>
      </div>
      <p className="text-sm text-slate-400 mb-4">Point your ad's landing-page form here and leads drop straight into your Seller's Inbox (honeypot + rate-limit protected; UTM captured for ROI).</p>
      <div className="space-y-2 mb-4">
        <FieldRow label="Endpoint" value={cfg.endpoint} onCopy={() => copy(cfg.endpoint, "Endpoint")} testid="lead-endpoint" />
        <FieldRow label="Capture key" value={cfg.key} onCopy={() => copy(cfg.key, "Key")} testid="lead-key" />
      </div>
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Copy-paste HTML form (UTM + honeypot wired)</span>
        <button data-testid="copy-snippet-btn" onClick={() => copy(snippet, "HTML snippet")} className="font-mono text-[10px] uppercase tracking-wider text-cyan-400 hover:text-cyan-300">Copy snippet</button>
      </div>
      <pre className="text-[10px] leading-relaxed text-slate-400 bg-[#080B11] border border-slate-800 rounded-md p-3 overflow-x-auto max-h-52">{snippet}</pre>
    </div>
  );
}

function FieldRow({ label, value, onCopy, testid }) {
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500 w-24 shrink-0">{label}</span>
      <code className="flex-1 text-[11px] text-cyan-400 bg-[#080B11] border border-slate-800 rounded px-2 py-1.5 truncate" data-testid={testid}>{value}</code>
      <button onClick={onCopy} className="font-mono text-[10px] uppercase tracking-wider text-slate-400 hover:text-cyan-400">Copy</button>
    </div>
  );
}
